/**
 * Serviço da Plataforma (website institucional e painel super admin)
 *
 * @author Equipe VendeAi
 * @since 1.1.0
 */

import { api } from './api';

export interface PlanLimits {
  maxUsers?: number | null;
  maxProducts?: number | null;
  maxOrdersPerMonth?: number | null;
  /** Espaço das mídias recebidas no WhatsApp (MB, #275); sem valor = 5 GB */
  maxWhatsappMediaMb?: number | null;
  /** Mídias guardadas por contato por dia (#281); sem valor = 50 arquivos e 300 MB */
  maxWhatsappMediaContactFiles?: number | null;
  maxWhatsappMediaContactMb?: number | null;
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  priceMonthly: number;
  priceYearly: number | null;
  setupFee: number;
  trialDays: number;
  isTrial: boolean;
  features: string[];
  limits: PlanLimits | null;
  /** Módulos adicionais liberados (null = todos) */
  modules?: string[] | null;
  highlight: boolean;
  badge: string | null;
  isPublic: boolean;
  isActive: boolean;
  sortOrder: number;
  tenantsCount?: number;
}

export interface PublicPlatformInfo {
  platformName: string;
  description: string;
  supportEmail: string;
  supportPhone: string | null;
  supportWhatsApp: string | null;
  termsUrl: string | null;
  privacyUrl: string | null;
  allowNewRegistrations: boolean;
  defaultTrialDays: number;
  /** Google Analytics 4 definido no painel Super Admin */
  gaMeasurementId?: string | null;
  /** Perfis oficiais definidos no painel Super Admin */
  socialProfiles?: string[];
}

export interface SalesLeadInput {
  name: string;
  email: string;
  phone: string;
  companyName?: string;
  segment?: string;
  planSlug?: string;
  message?: string;
  acceptContact: boolean;
  /** Honeypot anti-spam (deve ficar vazio) */
  website?: string;
}

export type SalesLeadStatus = 'NEW' | 'CONTACTED' | 'CONVERTED' | 'LOST';

export interface SalesLead {
  id: string;
  name: string;
  email: string;
  phone: string;
  companyName: string | null;
  segment: string | null;
  planSlug: string | null;
  message: string | null;
  source: string;
  status: SalesLeadStatus;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Planos ativos exibidos no site */
export async function getPublicPlans(): Promise<SubscriptionPlan[]> {
  const res = await api.get('/public/plans');
  return res.data.data as SubscriptionPlan[];
}

/** Informações públicas da plataforma */
export async function getPlatformInfo(): Promise<PublicPlatformInfo> {
  const res = await api.get('/public/platform');
  return res.data.data as PublicPlatformInfo;
}

/** Envia contato comercial do site */
export async function sendSalesLead(data: SalesLeadInput): Promise<string> {
  const res = await api.post('/public/leads', data);
  return res.data.message as string;
}

/** Formata valor em reais */
export function formatBRL(value: number, options: { cents?: boolean } = {}): string {
  const cents = options.cents ?? true;
  return value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: cents ? 2 : 0,
    maximumFractionDigits: cents ? 2 : 0,
  });
}

/** Extrai mensagem de erro padronizada da API */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  const e = error as {
    response?: {
      data?: {
        error?: {
          message?: string;
          errors?: { message: string }[];
          details?: { errors?: { message: string }[] };
        };
      };
    };
  };
  const apiError = e?.response?.data?.error;
  const fieldErrors = apiError?.errors || apiError?.details?.errors;
  if (fieldErrors && fieldErrors.length > 0 && fieldErrors[0]) {
    return fieldErrors[0].message;
  }
  return apiError?.message || fallback;
}

// =========================================================================
// Assinatura da plataforma (cobrança recorrente do estabelecimento)
// =========================================================================

export type BillingCycle = 'MONTHLY' | 'YEARLY';
export type BillingSubscriptionStatus = 'PENDING' | 'AUTHORIZED' | 'PAUSED' | 'CANCELLED';
export type TenantSubscriptionStatus = 'TRIAL' | 'ACTIVE' | 'PAST_DUE' | 'EXPIRED' | 'CANCELLED';

export interface BillingSummary {
  gatewayEnabled: boolean;
  /** Gateway exige CPF/CNPJ e o estabelecimento ainda não tem cadastrado */
  documentRequired: boolean;
  tenant: {
    status: TenantSubscriptionStatus;
    endsAt: string | null;
    daysLeft: number | null;
    plan: { id: string; name: string; slug: string } | null;
  };
  subscription: {
    id: string;
    status: BillingSubscriptionStatus;
    cycle: BillingCycle;
    amount: number;
    planName: string;
    nextPaymentDate: string | null;
    checkoutUrl: string | null;
    createdAt: string;
  } | null;
  plans: SubscriptionPlan[];
}

export async function getBillingSummary(): Promise<BillingSummary> {
  const res = await api.get('/billing');
  return res.data.data as BillingSummary;
}

export interface CheckoutResult {
  checkoutUrl: string | null;
  subscriptionId: string;
  firstPaymentDate: string;
}

export async function startBillingCheckout(
  planSlug: string,
  cycle: BillingCycle,
  document?: string,
): Promise<CheckoutResult> {
  const res = await api.post('/billing/checkout', {
    planSlug,
    cycle,
    ...(document ? { document } : {}),
  });
  return res.data.data as CheckoutResult;
}

export async function syncBilling(): Promise<BillingSummary> {
  const res = await api.post('/billing/sync');
  return res.data.data as BillingSummary;
}

export async function cancelBilling(): Promise<BillingSummary> {
  const res = await api.post('/billing/cancel');
  return res.data.data as BillingSummary;
}

// =========================================================================
// Depoimentos do website
// =========================================================================

export interface Testimonial {
  id: string;
  authorName: string;
  businessName: string;
  city: string | null;
  segment: string | null;
  quote: string;
  rating: number;
  avatarUrl: string | null;
  isPublished?: boolean;
  sortOrder?: number;
}

/** Depoimentos publicados (website) */
export async function getPublishedTestimonials(): Promise<Testimonial[]> {
  const res = await api.get('/public/testimonials');
  return res.data.data as Testimonial[];
}
